/**
 * useGeminiLiveTranslationSession — the Gemini transport for Live Translation.
 * Implements the SAME SessionHandle contract as useLiveTranslationSession
 * (the OpenAI/WebRTC hook), so LiveTranslationProvider + the modal/pill are
 * provider-agnostic; the active hook is chosen at build time in
 * useActiveLiveTranslationSession.
 *
 * Why two connections: gemini-3.5-live-translate-preview outputs ONE fixed
 * language per session. Bidirectional translation on a single phone therefore
 * needs TWO sessions —
 *   • "nurse" channel   (targetLanguageCode "en"): patient speech → English
 *   • "patient" channel (targetLanguageCode "es"…): nurse speech → patient lang
 * Both get the same mic. echoTargetLanguage:false means each session stays
 * silent when the input is already its target, so for any given utterance only
 * the opposite-direction session speaks. Both feed one speaker.
 *
 * Flow per channel:
 *   1. POST /…/sessions → { provider:"gemini", token, wsUrl, directions, … }
 *   2. open WS  wsUrl?access_token=<token>   (one per direction)
 *   3. send `setup` (model + translationConfig + transcription)
 *   4. on `setupComplete` mark ready; when BOTH ready → session-active
 *   5. mic chunks (PCM16/16k, half-duplex-gated) → realtimeInput on both WS
 *   6. serverContent audio (PCM16/24k) → shared playback; outputTranscription
 *      → transcript turns; turnComplete flushes the turn
 *   7. `goAway` / unexpected close → re-mint + reopen (15-min audio-session cap)
 *
 * Raw PCM has no hardware echo cancellation, so geminiAudioBridge runs
 * half-duplex (mic muted while the speaker is active). See that file.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { AppState, type AppStateStatus } from "react-native";

import { createGeminiAudioBridge, type GeminiAudioBridge } from "./geminiAudioBridge";
import type { ModalStatus, OpenSessionInput, TranscriptTurn } from "./types";
import type { SessionHandle } from "./useLiveTranslationSession";

import {
    appendLiveTranslationTurns,
    createLiveTranslationSession,
    endLiveTranslationSession,
    type LiveTranslationTurnInput
} from "@/lib/api";

const FLUSH_INTERVAL_MS = 5_000;
const RECONNECT_DELAY_MS = 800;
// Both sockets must report setupComplete within this window or we treat the
// connect as failed (bad token, network, model rejected the config). Nurses
// work from patient homes on weak/variable wifi, and the token-mint + WS
// handshake + setupComplete round-trip is heavier than a WebRTC ICE start, so
// keep this generous — a too-tight window was the #1 "Translation couldn't
// start" cause in the field.
const SETUP_TIMEOUT_MS = 20_000;
// Give up after this many consecutive failed (re)connects, after which the
// caller (useGeminiWithOpenAiFallback) drops to the OpenAI/WebRTC transport.
const MAX_CONSECUTIVE_FAILURES = 3;

// Interpreter mode: ONE session, a regular Live model driven by a bidirectional
// prompt. ALL config (model, prompt, voice, VAD) is built server-side and
// returned in `session.setup`; the ephemeral token is minted constrained to it.
// The mobile just opens the Constrained endpoint with the token and sends
// `setup` verbatim — no API key, no model/prompt knowledge on the client.

type StartInput = OpenSessionInput & { targetLanguage: string };

interface GeminiChannel {
    ws: WebSocket | null;
    outputRole: "nurse" | "patient";
    targetLanguageCode: string;
    ready: boolean;
    /** Accumulated outputTranscription text for the in-flight turn. */
    transcriptBuffer: string;
}

interface InternalRefs {
    channels: GeminiChannel[];
    bridge: GeminiAudioBridge | null;
    sessionId: number | null;
    visitId: string | null;
    inputSampleRate: number;
    pendingTurns: LiveTranslationTurnInput[];
    displayTurns: TranscriptTurn[];
    lastInput: StartInput | null;
    flushTimer: ReturnType<typeof setInterval> | null;
    tickTimer: ReturnType<typeof setInterval> | null;
    setupTimer: ReturnType<typeof setTimeout> | null;
    startedAtMs: number | null;
    userStopped: boolean;
    reconnecting: boolean;
    consecutiveFailures: number;
}

function makeRefs(): InternalRefs {
    return {
        channels: [],
        bridge: null,
        sessionId: null,
        visitId: null,
        inputSampleRate: 16_000,
        pendingTurns: [],
        displayTurns: [],
        lastInput: null,
        flushTimer: null,
        tickTimer: null,
        setupTimer: null,
        startedAtMs: null,
        userStopped: false,
        reconnecting: false,
        consecutiveFailures: 0
    };
}

/** The transcript role is the ORIGINAL speaker, which is the opposite of the
 *  channel's output role: the "nurse" channel outputs English translations of
 *  what the PATIENT said; the "patient" channel outputs the patient-language
 *  translation of what the NURSE said. */
function speakerRoleFor(outputRole: "nurse" | "patient"): "patient" | "nurse" {
    return outputRole === "nurse" ? "patient" : "nurse";
}

export function useGeminiLiveTranslationSession(): SessionHandle {
    const [status, setStatus] = useState<ModalStatus>("idle");
    const [sessionId, setSessionId] = useState<number | null>(null);
    const [targetLanguage, setTargetLanguage] = useState<string | null>(null);
    const [turns, setTurns] = useState<TranscriptTurn[]>([]);
    const [durationMs, setDurationMs] = useState(0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const refs = useRef<InternalRefs>(makeRefs());

    const closeSockets = useCallback(() => {
        const r = refs.current;
        for (const ch of r.channels) {
            if (ch.ws) {
                try {
                    ch.ws.onopen = null;
                    ch.ws.onmessage = null;
                    ch.ws.onerror = null;
                    ch.ws.onclose = null;
                    ch.ws.close();
                } catch {
                    // ignore
                }
                ch.ws = null;
            }
            ch.ready = false;
        }
    }, []);

    const teardown = useCallback(
        (opts: { keepBridge?: boolean } = {}) => {
            const r = refs.current;
            if (r.setupTimer) {
                clearTimeout(r.setupTimer);
                r.setupTimer = null;
            }
            if (!opts.keepBridge) {
                if (r.flushTimer) {
                    clearInterval(r.flushTimer);
                    r.flushTimer = null;
                }
                if (r.tickTimer) {
                    clearInterval(r.tickTimer);
                    r.tickTimer = null;
                }
            }
            closeSockets();
            if (!opts.keepBridge && r.bridge) {
                void r.bridge.stop();
                r.bridge = null;
            }
        },
        [closeSockets]
    );

    const reset = useCallback(() => {
        teardown();
        refs.current = makeRefs();
        setStatus("idle");
        setSessionId(null);
        setTargetLanguage(null);
        setTurns([]);
        setDurationMs(0);
        setErrorMessage(null);
    }, [teardown]);

    const flushTurns = useCallback(async () => {
        const r = refs.current;
        if (!r.visitId || !r.sessionId || r.pendingTurns.length === 0) {
            return;
        }
        const batch = r.pendingTurns;
        r.pendingTurns = [];
        try {
            const res = await appendLiveTranslationTurns(r.visitId, r.sessionId, batch);
            if (res.sessionExpired) {
                r.userStopped = true;
                teardown();
                setStatus("error");
                setErrorMessage("Session ended (duration limit reached)");
            }
        } catch (e) {
            if (r.pendingTurns.length + batch.length <= 200) {
                r.pendingTurns = [...batch, ...r.pendingTurns];
            }
            console.warn("[gemini-translation] flush failed", e);
        }
    }, [teardown]);

    const pushTurn = useCallback((role: "patient" | "nurse", text: string) => {
        const trimmed = text.trim();
        if (!trimmed) {
            return;
        }
        const startedAtMs = Date.now();
        const displayTurn: TranscriptTurn = {
            id: `${startedAtMs}-${Math.random().toString(36).slice(2, 8)}`,
            role,
            text: trimmed,
            startedAtMs
        };
        refs.current.displayTurns = [...refs.current.displayTurns, displayTurn];
        refs.current.pendingTurns = [
            ...refs.current.pendingTurns,
            { role, text: trimmed, startedAt: new Date(startedAtMs).toISOString() }
        ];
        setTurns([...refs.current.displayTurns]);
    }, []);

    // Declared up here so socket handlers can trigger a reconnect; the latest
    // `connect` is assigned into it on every render (they're mutually
    // recursive: connect opens sockets whose handlers may call reconnect).
    const connectRef = useRef<((input: StartInput, isReconnect: boolean) => Promise<void>) | null>(
        null
    );

    const scheduleReconnect = useCallback(() => {
        const r = refs.current;
        if (r.userStopped || r.reconnecting || !r.lastInput) {
            return;
        }
        r.reconnecting = true;
        setTimeout(() => {
            const input = r.lastInput;
            if (!input || r.userStopped) {
                r.reconnecting = false;
                return;
            }
            void connectRef
                .current?.(input, true)
                .catch((e) => {
                    console.warn("[gemini-translation] reconnect failed", e);
                })
                .finally(() => {
                    r.reconnecting = false;
                });
        }, RECONNECT_DELAY_MS);
    }, []);

    // Promote to active once BOTH channels have completed setup.
    const maybeActivate = useCallback(() => {
        const r = refs.current;
        // Interpreter mode has 1 channel; translate mode has 2. Activate once
        // every opened channel has reported setupComplete.
        if (r.channels.length > 0 && r.channels.every((c) => c.ready)) {
            if (r.setupTimer) {
                clearTimeout(r.setupTimer);
                r.setupTimer = null;
            }
            r.consecutiveFailures = 0;
            if (r.startedAtMs === null) {
                r.startedAtMs = Date.now();
            }
            setStatus("session-active");
            if (!r.flushTimer) {
                r.flushTimer = setInterval(() => void flushTurns(), FLUSH_INTERVAL_MS);
            }
            if (!r.tickTimer) {
                r.tickTimer = setInterval(() => {
                    if (r.startedAtMs) {
                        setDurationMs(Date.now() - r.startedAtMs);
                    }
                }, 1_000);
            }
        }
    }, [flushTurns]);

    const handleServerMessage = useCallback(
        (channel: GeminiChannel, raw: string) => {
            let msg: unknown;
            try {
                msg = JSON.parse(raw);
            } catch {
                return;
            }
            if (typeof msg !== "object" || msg === null) {
                return;
            }
            const m = msg as {
                setupComplete?: unknown;
                serverContent?: {
                    modelTurn?: { parts?: Array<{ inlineData?: { data?: string } }> };
                    outputTranscription?: { text?: string };
                    turnComplete?: boolean;
                };
                goAway?: unknown;
            };

            if (m.setupComplete !== undefined) {
                channel.ready = true;
                maybeActivate();
                return;
            }

            const content = m.serverContent;
            if (content) {
                // Interpreter mode is a single session → one audio stream; play
                // every audio part (the bridge skips near-silent frames).
                const parts = content.modelTurn?.parts ?? [];
                for (const part of parts) {
                    const data = part.inlineData?.data;
                    if (data) {
                        refs.current.bridge?.enqueuePlayback(data);
                    }
                }
                const text = content.outputTranscription?.text;
                if (typeof text === "string" && text.length > 0) {
                    channel.transcriptBuffer += text;
                }
                if (content.turnComplete && channel.transcriptBuffer) {
                    pushTurn(speakerRoleFor(channel.outputRole), channel.transcriptBuffer);
                    channel.transcriptBuffer = "";
                }
            }

            if (m.goAway !== undefined) {
                // The server is about to drop this connection (session-length
                // limit). Re-mint + reopen so the conversation continues.
                scheduleReconnect();
            }
        },
        [maybeActivate, pushTurn, scheduleReconnect]
    );

    const handleConnectFailure = useCallback(
        (e: unknown) => {
            const r = refs.current;
            r.consecutiveFailures += 1;
            if (r.consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
                r.userStopped = true;
                teardown();
                setStatus("error");
                setErrorMessage(
                    e instanceof Error ? e.message : "Could not connect to live translation"
                );
                if (r.sessionId && r.visitId) {
                    void endLiveTranslationSession(r.visitId, r.sessionId, {
                        status: "failed",
                        endedAt: new Date().toISOString(),
                        errorMessage: e instanceof Error ? e.message : "connect failed"
                    }).catch(() => undefined);
                }
            } else {
                scheduleReconnect();
            }
        },
        [scheduleReconnect, teardown]
    );

    const connect = useCallback(
        async (input: StartInput, isReconnect: boolean): Promise<void> => {
            const r = refs.current;
            r.lastInput = input;
            r.visitId = input.visitId;
            r.userStopped = false;

            setStatus("connecting");
            setErrorMessage(null);
            if (!isReconnect) {
                r.displayTurns = [];
                r.pendingTurns = [];
                setTurns([]);
                setDurationMs(0);
                r.startedAtMs = null;
            }
            setTargetLanguage(input.targetLanguage);

            // Fresh token + setup every (re)connect — tokens are short-lived.
            // Request gemini explicitly (this hook is only selected on binaries
            // with the native audio module); token + setup are minted server-side
            // and matched to each other.
            const session = await createLiveTranslationSession(
                input.visitId,
                input.targetLanguage,
                "gemini"
            );
            if (session.provider !== "gemini") {
                throw new Error(
                    `Live translation provider mismatch: server returned "${session.provider}".`
                );
            }
            r.sessionId = session.sessionId;
            r.inputSampleRate = session.inputSampleRate;
            setSessionId(session.sessionId);

            // Start mic/playback once; reconnects keep the bridge alive so audio
            // doesn't drop while sockets re-establish.
            if (!r.bridge) {
                const bridge = createGeminiAudioBridge();
                r.bridge = bridge;
                await bridge.start((base64Pcm16) => {
                    const payload = JSON.stringify({
                        realtimeInput: {
                            audio: {
                                data: base64Pcm16,
                                mimeType: `audio/pcm;rate=${r.inputSampleRate}`
                            }
                        }
                    });
                    for (const ch of r.channels) {
                        if (ch.ws && ch.ws.readyState === 1 /* OPEN */) {
                            try {
                                ch.ws.send(payload);
                            } catch {
                                // socket raced closed — ignore, reconnect handles it
                            }
                        }
                    }
                });
            }

            // Rebuild channels for this connection. Interpreter mode = ONE session
            // (a regular Live model does both directions via the prompt); the
            // translate-model path uses one session per fixed-direction.
            closeSockets();
            // Interpreter mode = ONE session (a regular Live model translates both
            // directions via the server-built prompt in `session.setup`).
            r.channels = [
                {
                    ws: null,
                    outputRole: "patient",
                    targetLanguageCode: input.targetLanguage,
                    ready: false,
                    transcriptBuffer: ""
                }
            ];

            if (r.setupTimer) {
                clearTimeout(r.setupTimer);
            }
            r.setupTimer = setTimeout(() => {
                // Not both-ready in time → failed connect.
                if (!r.userStopped && !r.channels.every((c) => c.ready)) {
                    handleConnectFailure(new Error("Gemini setup timed out"));
                }
            }, SETUP_TIMEOUT_MS);

            for (const channel of r.channels) {
                // Constrained (ephemeral-token) endpoint. The token goes RAW on the
                // query string — encoding the `auth_tokens/` slash → close 1008.
                const url = `${session.wsUrl}?access_token=${session.token}`;
                const ws = new WebSocket(url);
                // Gemini sends server messages (setupComplete, audio, transcripts)
                // as BINARY frames — force ArrayBuffer so onmessage can decode them
                // (the platform default "blob" arrives as an object our string-only
                // handler dropped, which stalled setup forever).
                ws.binaryType = "arraybuffer";
                channel.ws = ws;
                ws.onopen = () => {
                    try {
                        // `setup` is built server-side and matched to the token's
                        // mint-time constraint — send it verbatim.
                        ws.send(JSON.stringify({ setup: session.setup }));
                    } catch (e) {
                        console.warn("[gemini-translation] setup send failed", e);
                    }
                };
                ws.onmessage = (ev) => {
                    const data = (ev as { data: unknown }).data;
                    if (typeof data === "string") {
                        handleServerMessage(channel, data);
                    } else if (data instanceof ArrayBuffer) {
                        // Binary frame — the usual case for Gemini. Decode the
                        // UTF-8 JSON payload before handing it to the parser.
                        handleServerMessage(channel, new TextDecoder().decode(data));
                    }
                };
                ws.onerror = () => {
                    // onclose follows; reconnect is handled there.
                };
                ws.onclose = () => {
                    channel.ready = false;
                    if (!r.userStopped && r.startedAtMs !== null) {
                        // Was live and the socket dropped → reconnect.
                        scheduleReconnect();
                    }
                };
            }
        },
        [closeSockets, handleConnectFailure, handleServerMessage, scheduleReconnect]
    );

    connectRef.current = connect;

    const start = useCallback(
        async (input: StartInput) => {
            try {
                await connect(input, false);
            } catch (e) {
                handleConnectFailure(e);
            }
        },
        [connect, handleConnectFailure]
    );

    const stop = useCallback(async () => {
        const r = refs.current;
        r.userStopped = true;
        teardown();
        await flushTurns();
        if (r.visitId && r.sessionId) {
            try {
                await endLiveTranslationSession(r.visitId, r.sessionId, {
                    status: "completed",
                    endedAt: new Date().toISOString()
                });
            } catch (e) {
                console.warn("[gemini-translation] finalize failed", e);
            }
        }
        setStatus("idle");
    }, [flushTurns, teardown]);

    useEffect(() => {
        return () => {
            teardown();
        };
    }, [teardown]);

    // Stop when backgrounded — translating into a pocket burns credit, and iOS
    // suspends the WS + audio session within seconds anyway.
    useEffect(() => {
        const handler = (state: AppStateStatus) => {
            if (state === "background" && refs.current.channels.some((c) => c.ws)) {
                void stop();
            }
        };
        const sub = AppState.addEventListener("change", handler);
        return () => sub.remove();
    }, [stop]);

    return {
        status,
        sessionId,
        targetLanguage,
        turns,
        durationMs,
        errorMessage,
        start,
        stop,
        reset
    };
}
